/**
 * NameDialogUI ("Me") lets a mate edit their name/avatar and, when in a
 * videoconferencing-enabled room, pick their camera/mic.
 * Uses list navigation for keyboard/gamepad control and integrates with focus navigation system.
 */
import * as React from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent, useTabs, cn } from '@merels/ui'
import { IconButton } from '../../../library/IconButton/IconButton'
import { CloseIcon } from '../../../library/Icons/CloseIcon'
import { useFocusNavigationDep, useControllerInputDep } from '../../../providers/FocusNavigationProvider/FocusNavigationProvider'
import { useSignals } from '@preact/signals-react/runtime'
import { useListNavigation } from '../../../../hooks/useListNavigation'
import { NameAndAvatarTab, type NameAndAvatarTabProps } from './NameAndAvatarTab/NameAndAvatarTab'
import { AudioVideoTab, type AudioVideoTabProps } from './AudioVideoTab/AudioVideoTab'
import { AVATAR_PRESETS, isPresetThumbnailValue } from '../../../../assets/avatarPresets/avatarPresets'

export interface NameDialogUIProps
  extends Omit<NameAndAvatarTabProps, 'videoEnabled' | 'registerItem' | 'focusedIndex' | 'baseIndex'>,
    Omit<AudioVideoTabProps, 'videoEnabled' | 'registerItem' | 'focusedIndex' | 'baseIndex'> {
  isOpen: boolean
  onClose: () => void
  /** Whether Daily.co videoconferencing is enabled for the current room - grays out Audio and video. */
  videoEnabled: boolean
  /** Shared-screen only: which controller this mate is currently mapped to (e.g. "Controller X1") - shown in the dialog title. */
  controllerLabel?: string
}

// Fixed prefix of the flat nav list, before whichever tab's own items.
const CLOSE_INDEX = 0
const NAME_AVATAR_TAB_INDEX = 1
const AUDIO_VIDEO_TAB_INDEX = 2
const TAB_CONTENT_BASE_INDEX = 3

export function NameDialogUI({
  isOpen,
  onClose,
  videoEnabled,
  name,
  onNameChange,
  thumbnailUrl,
  thumbnailHistory,
  onThumbnailUpload,
  onThumbnailSelect,
  onThumbnailDelete,
  onThumbnailClear,
  isSharedScreen,
  onSwitchProfile,
  controllerLabel,
  isOwner,
  onToggleVideoEnabled,
  cameras,
  mics,
  selectedCameraId,
  selectedMicId,
  onSelectCamera,
  onSelectMic,
}: NameDialogUIProps) {
  useSignals()
  const focusNavigation = useFocusNavigationDep()
  const controllerInput = useControllerInputDep()
  const isFocusNavActive = focusNavigation.isActive.value
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  const { activeTab, setActiveTab } = useTabs('name-avatar')
  const [showPresetGrid, setShowPresetGrid] = React.useState(false)

  // How many focusable controls the *active* tab renders - must mirror exactly the
  // conditional item lists NameAndAvatarTab/AudioVideoTab build for themselves (see
  // their own local index allocation), since useListNavigation only needs a count to
  // drive up/down/select over the single flat list this dialog exposes to a controller.
  const activeTabItemCount = React.useMemo(() => {
    if (activeTab === 'name-avatar') {
      let count = 1 // edit-name
      if (!isSharedScreen) count += 1 // upload-photo (shared-screen mates pick from presets instead)
      if (thumbnailUrl) count += 1 // use-initials
      if (isSharedScreen && onSwitchProfile) count += 1 // switch-profile
      count += 1 // collapsed built-in avatar stack trigger
      if (showPresetGrid) count += AVATAR_PRESETS.length // expanded preset grid
      count += thumbnailHistory.filter((url) => url !== thumbnailUrl && !isPresetThumbnailValue(url)).length
      return count
    }
    if (!videoEnabled) return isOwner ? 1 : 0
    if (cameras.length === 0 && mics.length === 0) return 0
    return cameras.length + mics.length
  }, [activeTab, thumbnailUrl, isSharedScreen, onSwitchProfile, showPresetGrid, thumbnailHistory, videoEnabled, isOwner, cameras.length, mics.length])

  const navItemCount = TAB_CONTENT_BASE_INDEX + activeTabItemCount
  const navItems = React.useMemo(() => Array.from({ length: navItemCount }, (_, i) => i), [navItemCount])

  // Every item behaves like "click whatever DOM control is focused" - each control
  // already has the right onClick handler, so this one dispatch covers Close, both
  // tab triggers, and every control either tab renders (see NameAndAvatarTab /
  // AudioVideoTab), without NameDialogUI needing to know per-item semantics.
  const itemElsRef = React.useRef<(HTMLElement | null)[]>([])
  const handleSelectItem = React.useCallback((_item: number, index: number) => {
    itemElsRef.current[index]?.click()
  }, [])

  // Flat-list stepping (see useListNavigation) is right for most of this dialog, but
  // the tab row needs 2D-ish behavior: left/right cycles Close <-> the two tabs, while
  // down "drills into" whichever tab is focused (switching to it first if needed) -
  // rather than treating the tab row as just more flat-list items to step past.
  const computeNextIndex = React.useCallback((current: number, direction: 'up' | 'down' | 'left' | 'right', itemsLength: number): number => {
    const isTabTrigger = current === NAME_AVATAR_TAB_INDEX || current === AUDIO_VIDEO_TAB_INDEX

    if (isTabTrigger && direction === 'down') {
      setActiveTab(current === NAME_AVATAR_TAB_INDEX ? 'name-avatar' : 'audio-video')
      return itemsLength > TAB_CONTENT_BASE_INDEX ? TAB_CONTENT_BASE_INDEX : current
    }

    const isTopRow = current === CLOSE_INDEX || isTabTrigger
    if (isTopRow) {
      if (direction === 'down') {
        // Close has no tab of its own - drop into whichever tab is already active.
        return itemsLength > TAB_CONTENT_BASE_INDEX ? TAB_CONTENT_BASE_INDEX : current
      }
      if (direction === 'up') {
        return current
      }
      const topRow = [CLOSE_INDEX, NAME_AVATAR_TAB_INDEX, AUDIO_VIDEO_TAB_INDEX]
      const idx = topRow.indexOf(current)
      const nextIdx = direction === 'right' ? (idx + 1) % topRow.length : (idx - 1 + topRow.length) % topRow.length
      return topRow[nextIdx]
    }

    // Inside the active tab's content - up from the first item returns to that tab's
    // own trigger (not the previous flat-list index, which may be the *other* tab).
    if (current === TAB_CONTENT_BASE_INDEX && direction === 'up') {
      return activeTab === 'name-avatar' ? NAME_AVATAR_TAB_INDEX : AUDIO_VIDEO_TAB_INDEX
    }
    if (direction === 'up' || direction === 'left') {
      return current > TAB_CONTENT_BASE_INDEX ? current - 1 : current
    }
    return current < itemsLength - 1 ? current + 1 : current
  }, [activeTab, setActiveTab])

  const nav = useListNavigation(navItems, handleSelectItem, isOpen && isFocusNavActive, undefined, false, {
    // Start on the name field (first item of the default "Name and Avatar" tab) so
    // controller users land somewhere useful without an extra navigation step.
    initialIndex: TAB_CONTENT_BASE_INDEX,
    computeNextIndex,
  })

  const registerItem = React.useCallback((index: number, el: HTMLElement | null) => {
    itemElsRef.current[index] = el
    nav.registerItem(index, el)
  }, [nav])

  React.useEffect(() => {
    if (isOpen) registerItem(CLOSE_INDEX, closeButtonRef.current)
  }, [isOpen, registerItem])

  // While open, claim the 'dialog' focus area so neither the header nor game content
  // (e.g. the game-selection grid) also respond to the same navigate/select events -
  // otherwise both this dialog's own list nav and, say, the grid behind it would react
  // to the same keypress, and the grid's own focus-restoring effect would win DOM
  // focus away from this dialog's controls. Mirrors FocusNavigationDialog's pattern.
  React.useEffect(() => {
    if (!isFocusNavActive) return
    if (isOpen) {
      focusNavigation.setFocusArea('dialog')
    }
  }, [isOpen, isFocusNavActive, focusNavigation])

  const wasOpenRef = React.useRef(isOpen)
  React.useEffect(() => {
    if (wasOpenRef.current && !isOpen && isFocusNavActive) {
      focusNavigation.setFocusArea('content')
    }
    wasOpenRef.current = isOpen
  }, [isOpen, isFocusNavActive, focusNavigation])

  // Handle controller input for cancel/exit (select is handled by useListNavigation)
  React.useEffect(() => {
    if (!isOpen || !isFocusNavActive) return

    const unsubscribe = controllerInput.subscribe((event) => {
      if (event.type === 'cancel' || event.type === 'exit') {
        onClose()
      }
    })
    return unsubscribe
  }, [isOpen, isFocusNavActive, controllerInput, onClose])

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-overlay flex items-center justify-center z-[10000]" onClick={onClose}>
      <div
        className="w-full max-w-[480px] rounded-lg border border-basic bg-background p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-primary">
            Me{controllerLabel && ` (${controllerLabel})`}
          </h3>
          <IconButton
            ref={closeButtonRef}
            aria-label="Close"
            className={cn('text-muted hover:text-primary', nav.focusedIndex === CLOSE_INDEX && 'ring-2 ring-accent rounded')}
            icon={<CloseIcon width={16} height={16} />}
            onClick={onClose}
          />
        </div>

        <Tabs className="h-[420px]">
          <TabsList>
            <TabsTrigger
              ref={(el) => registerItem(NAME_AVATAR_TAB_INDEX, el)}
              value="name-avatar"
              active={activeTab === 'name-avatar'}
              onClick={() => setActiveTab('name-avatar')}
              className={cn(nav.focusedIndex === NAME_AVATAR_TAB_INDEX && 'ring-2 ring-accent')}
            >
              Name and Avatar
            </TabsTrigger>
            <TabsTrigger
              ref={(el) => registerItem(AUDIO_VIDEO_TAB_INDEX, el)}
              value="audio-video"
              active={activeTab === 'audio-video'}
              onClick={() => setActiveTab('audio-video')}
              className={cn(!videoEnabled && 'opacity-50', nav.focusedIndex === AUDIO_VIDEO_TAB_INDEX && 'ring-2 ring-accent')}
              title={videoEnabled ? undefined : 'Videoconferencing is off for this room'}
            >
              Audio and video
            </TabsTrigger>
          </TabsList>

          <TabsContent value="name-avatar" active={activeTab === 'name-avatar'} className="pt-4">
            <NameAndAvatarTab
              name={name}
              onNameChange={onNameChange}
              thumbnailUrl={thumbnailUrl}
              thumbnailHistory={thumbnailHistory}
              onThumbnailUpload={onThumbnailUpload}
              onThumbnailSelect={onThumbnailSelect}
              onThumbnailDelete={onThumbnailDelete}
              onThumbnailClear={onThumbnailClear}
              videoEnabled={videoEnabled}
              isSharedScreen={isSharedScreen}
              onSwitchProfile={onSwitchProfile}
              registerItem={registerItem}
              focusedIndex={nav.focusedIndex}
              baseIndex={TAB_CONTENT_BASE_INDEX}
              showPresetGrid={showPresetGrid}
              onShowPresetGridChange={setShowPresetGrid}
            />
          </TabsContent>

          <TabsContent value="audio-video" active={activeTab === 'audio-video'} className="pt-4">
            <AudioVideoTab
              videoEnabled={videoEnabled}
              isOwner={isOwner}
              onToggleVideoEnabled={onToggleVideoEnabled}
              cameras={cameras}
              mics={mics}
              selectedCameraId={selectedCameraId}
              selectedMicId={selectedMicId}
              onSelectCamera={onSelectCamera}
              onSelectMic={onSelectMic}
              registerItem={registerItem}
              focusedIndex={nav.focusedIndex}
              baseIndex={TAB_CONTENT_BASE_INDEX}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
